import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Box, Text, Badge, Flex, Select, Input,
  Table, Thead, Tbody, Tr, Th, Td, useToast, Icon, Alert, AlertIcon, Spinner,
  Tabs, TabList, Tab, TabPanels, TabPanel, Switch,
} from '@chakra-ui/react';
import { FiDollarSign, FiUpload, FiCheckCircle, FiRefreshCw } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const fmtMoney = (v) => `£${parseFloat(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ── Parse a date value from various formats ──────────────────────────────────
const parseDate = (val) => {
  if (!val) return null;
  // Excel serial number
  if (typeof val === 'number') {
    const d = new Date(Math.round((val - 25569) * 86400 * 1000));
    return d.toISOString().split('T')[0];
  }
  const s = String(val).trim();
  // DD/MM/YYYY or DD-MM-YYYY
  const dmy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (dmy) {
    const y = dmy[3].length === 2 ? '20' + dmy[3] : dmy[3];
    return `${y}-${dmy[2].padStart(2,'0')}-${dmy[1].padStart(2,'0')}`;
  }
  // ISO
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return iso[0];
  return null;
};

// ── Parse amount — handle parentheses for negatives, strip £ commas ─────────
const parseAmount = (val) => {
  if (val === null || val === undefined || val === '') return null;
  const s = String(val).trim().replace(/[£,\s]/g, '');
  if (!s || s === '-') return null;
  // Parentheses = negative
  const neg = s.match(/^\((.+)\)$/);
  const n = parseFloat(neg ? neg[1] : s);
  return isNaN(n) ? null : (neg ? -n : n);
};

// ── Try to detect columns from a header row ──────────────────────────────────
const detectColumns = (headers) => {
  const h = headers.map((x, i) => ({ idx: i, key: String(x||'').toLowerCase().trim() }));
  const find = (...terms) => (h.find(x => terms.some(t => x.key.includes(t)))?.idx ?? -1);
  return {
    date:    find('date','posted','transaction date','tran date','value date'),
    desc:    find('description','narrative','details','payee','memo','particulars'),
    ref:     find('reference','ref','cheque','check no'),
    debit:   find('debit','out','withdrawal','payments out','dr'),
    credit:  find('credit','in','deposit','payments in','cr'),
    balance: find('balance','running balance','closing balance'),
    amount:  find('amount','value'), // single amount column fallback
  };
};

// ── Parse any spreadsheet/CSV file into normalised rows ───────────────────────
const parseFile = (input, isBuffer) => {
  const wb = XLSX.read(input, { type: isBuffer ? 'array' : 'string', cellDates: false });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  if (raw.length < 2) return [];

  // Find header row (first row with 3+ non-empty cells)
  let headerIdx = 0;
  for (let i = 0; i < Math.min(10, raw.length); i++) {
    if (raw[i].filter(c => String(c).trim()).length >= 3) { headerIdx = i; break; }
  }
  const headers = raw[headerIdx];
  const cols = detectColumns(headers);
  const results = [];

  for (let i = headerIdx + 1; i < raw.length; i++) {
    const row = raw[i];
    if (row.every(c => !String(c).trim())) continue; // skip blank rows

    const dateVal = parseDate(row[cols.date]);
    if (!dateVal) continue;

    let debit = null, credit = null;
    if (cols.debit >= 0 || cols.credit >= 0) {
      debit  = cols.debit  >= 0 ? parseAmount(row[cols.debit])  : null;
      credit = cols.credit >= 0 ? parseAmount(row[cols.credit]) : null;
    } else if (cols.amount >= 0) {
      const amt = parseAmount(row[cols.amount]);
      if (amt !== null) { if (amt < 0) debit = Math.abs(amt); else credit = amt; }
    }

    results.push({
      entry_date:    dateVal,
      description:   cols.desc  >= 0 ? String(row[cols.desc]  || '').trim().substring(0, 500) : '',
      reference:     cols.ref   >= 0 ? String(row[cols.ref]   || '').trim().substring(0, 255) : '',
      debit_amount:  debit  !== null ? Math.abs(debit)  : null,
      credit_amount: credit !== null ? Math.abs(credit) : null,
      balance:       cols.balance >= 0 ? parseAmount(row[cols.balance]) : null,
      raw_row:       JSON.stringify(row),
    });
  }
  return results;
};

export default function BankLedgerDashboard({ isOpen, onClose }) {
  const { user } = useAuth();
  const canImport = ['admin','accounts_manager'].includes(user?.role);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [ledger, setLedger] = useState({ transactions: [], total_in: 0, total_out: 0 });
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [showUnreconciledOnly, setShowUnreconciledOnly] = useState(false);

  // CSV import state
  const [importRows, setImportRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef();
  const toast = useToast();

  const fetchAccounts = useCallback(async () => {
    try { const { data } = await api.get('/bank-accounts?is_own=true'); setAccounts(data); } catch(e) {}
  }, []);

  const fetchLedger = useCallback(async () => {
    if (!selectedAccount) { setLedger({ transactions: [], total_in: 0, total_out: 0 }); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('from', dateFrom);
      if (dateTo) params.set('to', dateTo);
      const { data } = await api.get(`/bank-accounts/ledger/${selectedAccount}?${params.toString()}`);
      setLedger(data);
    } catch(e) { setLedger({ transactions: [], total_in: 0, total_out: 0 }); }
    finally { setLoading(false); }
  }, [selectedAccount, dateFrom, dateTo]);

  const fetchLedgerEntries = useCallback(async () => {
    if (!selectedAccount) { setLedgerEntries([]); setSummary(null); return; }
    setLoadingEntries(true);
    try {
      const params = new URLSearchParams();
      if (showUnreconciledOnly) params.set('reconciled', 'false');
      const [entriesRes, summaryRes] = await Promise.all([
        api.get(`/bank-accounts/${selectedAccount}/ledger-entries?${params}`),
        api.get(`/bank-accounts/${selectedAccount}/ledger-summary`),
      ]);
      setLedgerEntries(entriesRes.data);
      setSummary(summaryRes.data);
    } catch(e) { setLedgerEntries([]); }
    finally { setLoadingEntries(false); }
  }, [selectedAccount, showUnreconciledOnly]);

  useEffect(() => { if (isOpen) fetchAccounts(); }, [isOpen, fetchAccounts]);
  useEffect(() => { if (selectedAccount) { fetchLedger(); fetchLedgerEntries(); } }, [selectedAccount, fetchLedger, fetchLedgerEntries]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportResult(null);
    const isCSV = /\.(csv|txt)$/i.test(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const rows = parseFile(isCSV ? ev.target.result : new Uint8Array(ev.target.result), !isCSV);
        if (!rows.length) { toast({ title: 'No valid rows found', status: 'warning', duration: 3000 }); }
        setImportRows(rows);
      } catch(err) { toast({ title: 'Could not parse file: ' + err.message, status: 'error', duration: 4000 }); }
    };
    if (isCSV) reader.readAsText(file); else reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!selectedAccount || !importRows.length) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/bank-accounts/${selectedAccount}/ledger-entries/import`, {
        entries: importRows,
        import_id: `csv_${Date.now()}`,
      });
      setImportResult(data);
      toast({
        title: `Import complete — ${data.inserted} new entries, ${data.duplicates} duplicates skipped`,
        status: data.inserted > 0 ? 'success' : 'info', duration: 4000,
      });
      setImportRows([]);
      fetchLedgerEntries();
    } catch(e) { toast({ title: e.response?.data?.error || 'Import failed', status: 'error', duration: 3000 }); }
    setImporting(false);
  };

  const balance = ledger.total_in - ledger.total_out;
  const acct = accounts.find(a => a.id === parseInt(selectedAccount));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="90vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" align="center" pr={8}>
            <HStack><FiDollarSign /><Text>Bank Account Ledger</Text></HStack>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            {/* Account selector */}
            <HStack spacing={2}>
              <Select size="sm" value={selectedAccount} flex={2}
                onChange={e => { setSelectedAccount(e.target.value); setImportRows([]); setImportResult(null); }}>
                <option value="">Select an account...</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.account_name || a.bank_name} {a.currency_code ? `(${a.currency_code})` : ''} {a.account_number ? `— ${a.account_number}` : ''}
                  </option>
                ))}
              </Select>
              <Input size="sm" type="date" value={dateFrom} w="140px" onChange={e => setDateFrom(e.target.value)} />
              <Input size="sm" type="date" value={dateTo} w="140px" onChange={e => setDateTo(e.target.value)} />
              <Button size="sm" variant="outline" leftIcon={<FiRefreshCw size={13}/>}
                onClick={() => { fetchLedger(); fetchLedgerEntries(); }} isDisabled={!selectedAccount}>
                Refresh
              </Button>
            </HStack>

            {selectedAccount && acct && (
              <Tabs variant="line" colorScheme="blue" size="sm">
                <TabList>
                  <Tab>Payment Ledger</Tab>
                  <Tab>
                    Statement Entries
                    {summary?.total_entries > 0 && (
                      <Badge ml={2} colorScheme="blue" fontSize="8px">{summary.total_entries}</Badge>
                    )}
                    {summary?.unreconciled_count > 0 && (
                      <Badge ml={1} colorScheme="orange" fontSize="8px">{summary.unreconciled_count} unreconciled</Badge>
                    )}
                  </Tab>
                  {canImport && <Tab>Import CSV / Excel</Tab>}
                </TabList>
                <TabPanels>

                  {/* ── Tab 1: Payment ledger (existing) ── */}
                  <TabPanel px={0}>
                    <Flex bg="gray.50" p={3} borderRadius="md" gap={6} mb={3} flexWrap="wrap">
                      <Box>
                        <Text fontSize="10px" color="gray.400" fontWeight="700" textTransform="uppercase">Account</Text>
                        <Text fontSize="14px" fontWeight="700">{acct.account_name || acct.bank_name}</Text>
                        <Text fontSize="10px" color="gray.500">{acct.bank_name}{acct.sort_code ? ` — ${acct.sort_code}` : ''}{acct.account_number ? ` — ${acct.account_number}` : ''}</Text>
                      </Box>
                      <Box><Text fontSize="10px" color="gray.400" fontWeight="700" textTransform="uppercase">Money In</Text><Text fontSize="18px" fontWeight="800" color="green.600">{fmtMoney(ledger.total_in)}</Text></Box>
                      <Box><Text fontSize="10px" color="gray.400" fontWeight="700" textTransform="uppercase">Money Out</Text><Text fontSize="18px" fontWeight="800" color="red.500">{fmtMoney(ledger.total_out)}</Text></Box>
                      <Box><Text fontSize="10px" color="gray.400" fontWeight="700" textTransform="uppercase">Balance</Text><Text fontSize="18px" fontWeight="800" color={balance >= 0 ? 'green.700' : 'red.600'}>{fmtMoney(balance)}</Text></Box>
                    </Flex>
                    {loading && <Flex justify="center" py={6}><Spinner /></Flex>}
                    {!loading && ledger.transactions.length === 0 && <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>No payment transactions found.</Text>}
                    {!loading && ledger.transactions.length > 0 && (
                      <Table size="sm" variant="simple">
                        <Thead><Tr bg="gray.50">
                          <Th px={2} fontSize="9px">Date</Th><Th px={2} fontSize="9px">Invoice</Th>
                          <Th px={2} fontSize="9px">Reference</Th><Th px={2} fontSize="9px">From</Th>
                          <Th px={2} fontSize="9px">To</Th><Th px={2} fontSize="9px" isNumeric>In</Th>
                          <Th px={2} fontSize="9px" isNumeric>Out</Th>
                        </Tr></Thead>
                        <Tbody>
                          {ledger.transactions.map(t => {
                            const isIn = t.to_bank_account_id === parseInt(selectedAccount);
                            return (
                              <Tr key={t.id} _hover={{ bg: 'gray.50' }}>
                                <Td px={2} fontSize="11px">{fmtDate(t.payment_date)}</Td>
                                <Td px={2} fontSize="11px">{t.invoice_number || '—'}</Td>
                                <Td px={2} fontSize="11px" maxW="150px" isTruncated>{t.reference || t.notes || '—'}</Td>
                                <Td px={2} fontSize="11px">{t.from_account_name || t.from_bank_name || '—'}</Td>
                                <Td px={2} fontSize="11px">{t.to_account_name || t.to_bank_name || '—'}</Td>
                                <Td px={2} isNumeric>{isIn ? <Text fontSize="11px" fontWeight="600" color="green.600">{fmtMoney(t.amount)}</Text> : '—'}</Td>
                                <Td px={2} isNumeric>{!isIn ? <Text fontSize="11px" fontWeight="600" color="red.500">{fmtMoney(t.amount)}</Text> : '—'}</Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    )}
                  </TabPanel>

                  {/* ── Tab 2: Statement entries (CSV imported) ── */}
                  <TabPanel px={0}>
                    {summary && (
                      <Flex bg="gray.50" p={3} borderRadius="md" gap={6} mb={3} flexWrap="wrap" align="center" justify="space-between">
                        <HStack spacing={6}>
                          <Box><Text fontSize="10px" color="gray.400" fontWeight="700" textTransform="uppercase">Total Entries</Text><Text fontSize="16px" fontWeight="800">{summary.total_entries}</Text></Box>
                          <Box><Text fontSize="10px" color="gray.400" fontWeight="700" textTransform="uppercase">Total In</Text><Text fontSize="16px" fontWeight="800" color="green.600">{fmtMoney(summary.total_in)}</Text></Box>
                          <Box><Text fontSize="10px" color="gray.400" fontWeight="700" textTransform="uppercase">Total Out</Text><Text fontSize="16px" fontWeight="800" color="red.500">{fmtMoney(summary.total_out)}</Text></Box>
                          <Box><Text fontSize="10px" color="gray.400" fontWeight="700" textTransform="uppercase">Unreconciled</Text><Text fontSize="16px" fontWeight="800" color="orange.500">{summary.unreconciled_count}</Text></Box>
                          {summary.earliest_date && <Box><Text fontSize="10px" color="gray.400" fontWeight="700" textTransform="uppercase">Date Range</Text><Text fontSize="11px">{fmtDate(summary.earliest_date)} → {fmtDate(summary.latest_date)}</Text></Box>}
                        </HStack>
                        <HStack spacing={2}>
                          <Text fontSize="11px" color="gray.500">Unreconciled only</Text>
                          <Switch size="sm" isChecked={showUnreconciledOnly} colorScheme="orange"
                            onChange={e => setShowUnreconciledOnly(e.target.checked)} />
                        </HStack>
                      </Flex>
                    )}
                    {loadingEntries && <Flex justify="center" py={6}><Spinner /></Flex>}
                    {!loadingEntries && ledgerEntries.length === 0 && (
                      <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>
                        No statement entries yet. Use the "Import CSV / Excel" tab to upload bank statements.
                      </Text>
                    )}
                    {!loadingEntries && ledgerEntries.length > 0 && (
                      <Box overflowX="auto">
                        <Table size="sm">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th fontSize="9px" px={2}>Date</Th>
                              <Th fontSize="9px" px={2}>Description</Th>
                              <Th fontSize="9px" px={2}>Reference</Th>
                              <Th fontSize="9px" px={2} textAlign="right" color="green.600">Credit (In)</Th>
                              <Th fontSize="9px" px={2} textAlign="right" color="red.600">Debit (Out)</Th>
                              <Th fontSize="9px" px={2} textAlign="right">Balance</Th>
                              <Th fontSize="9px" px={2} textAlign="center">Reconciled</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {ledgerEntries.map(e => (
                              <Tr key={e.id} _hover={{ bg: 'gray.50' }}
                                bg={e.reconciled_at ? 'green.50' : 'white'}>
                                <Td px={2} fontSize="11px" whiteSpace="nowrap">{fmtDate(e.entry_date)}</Td>
                                <Td px={2} fontSize="11px" maxW="240px" isTruncated title={e.description}>{e.description || '—'}</Td>
                                <Td px={2} fontSize="10px" color="gray.500">{e.reference || '—'}</Td>
                                <Td px={2} textAlign="right">
                                  {e.credit_amount != null && <Text fontSize="11px" fontWeight="600" color="green.600">{fmtMoney(e.credit_amount)}</Text>}
                                </Td>
                                <Td px={2} textAlign="right">
                                  {e.debit_amount != null && <Text fontSize="11px" fontWeight="600" color="red.500">{fmtMoney(e.debit_amount)}</Text>}
                                </Td>
                                <Td px={2} textAlign="right">
                                  {e.balance != null && <Text fontSize="10px" color="gray.600">{fmtMoney(e.balance)}</Text>}
                                </Td>
                                <Td px={2} textAlign="center">
                                  {e.reconciled_at
                                    ? <Badge colorScheme="green" fontSize="8px">✓ Reconciled</Badge>
                                    : <Badge colorScheme="gray" variant="outline" fontSize="8px">Pending</Badge>}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </TabPanel>

                  {/* ── Tab 3: Import CSV (admin/accounts_manager only) ── */}
                  {canImport && (
                    <TabPanel px={0}>
                      <Box bg="blue.50" p={4} borderRadius="xl" border="1px solid" borderColor="blue.200" mb={4}>
                        <HStack mb={1} justify="space-between">
                          <Text fontSize="13px" fontWeight="700" color="blue.700">Import Bank Statement</Text>
                          <Badge colorScheme="blue" fontSize="9px">CSV · XLSX · XLS · ODS</Badge>
                        </HStack>
                        <Text fontSize="11px" color="blue.600" mb={3}>
                          Export your bank statement from your online banking and upload here.
                          The system automatically detects Date, Description, Debit, Credit and Balance columns.
                          Duplicate entries (same date, description and amount) are silently skipped —
                          you can safely re-upload the same file or upload overlapping date ranges.
                        </Text>

                        {importResult && (
                          <Alert status={importResult.inserted > 0 ? 'success' : 'info'} borderRadius="lg" mb={3} py={2}>
                            <AlertIcon boxSize={4} />
                            <Text fontSize="12px">
                              <strong>{importResult.inserted}</strong> new entries imported.
                              {importResult.duplicates > 0 && <Text as="span" color="gray.600"> {importResult.duplicates} duplicates skipped.</Text>}
                              {importResult.errors > 0 && <Text as="span" color="red.600"> {importResult.errors} errors.</Text>}
                            </Text>
                          </Alert>
                        )}

                        <input type="file" accept=".csv,.txt,.xlsx,.xls,.ods" ref={fileRef}
                          onChange={handleFileSelect} style={{ display: 'none' }} />

                        {importRows.length === 0 ? (
                          <Box border="2px dashed" borderColor="blue.300" borderRadius="xl" p={8}
                            textAlign="center" cursor="pointer"
                            _hover={{ borderColor: 'blue.500', bg: 'blue.100' }}
                            onClick={() => fileRef.current?.click()}>
                            <Icon as={FiUpload} boxSize={8} color="blue.400" mb={2} />
                            <Text fontSize="14px" fontWeight="600" color="blue.700">Click to choose your bank statement file</Text>
                            <Text fontSize="11px" color="blue.500" mt={1}>Supports CSV, Excel (.xlsx, .xls) and ODS</Text>
                          </Box>
                        ) : (
                          <Box>
                            <HStack mb={3} justify="space-between">
                              <HStack>
                                <Icon as={FiCheckCircle} color="blue.500" />
                                <Text fontSize="13px" fontWeight="600" color="blue.700">
                                  {importRows.length} rows ready — {importRows.filter(r => r.credit_amount != null).length} credits, {importRows.filter(r => r.debit_amount != null).length} debits
                                </Text>
                              </HStack>
                              <Button size="xs" variant="ghost" onClick={() => setImportRows([])}>Clear</Button>
                            </HStack>

                            {/* Preview first 10 rows */}
                            <Box border="1px" borderColor="blue.200" borderRadius="lg" overflow="hidden" mb={4}>
                              <Table size="xs">
                                <Thead bg="blue.100">
                                  <Tr>
                                    <Th fontSize="9px" px={2}>Date</Th>
                                    <Th fontSize="9px" px={2}>Description</Th>
                                    <Th fontSize="9px" px={2} textAlign="right" color="green.700">Credit</Th>
                                    <Th fontSize="9px" px={2} textAlign="right" color="red.700">Debit</Th>
                                    <Th fontSize="9px" px={2} textAlign="right">Balance</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {importRows.slice(0, 10).map((r, i) => (
                                    <Tr key={i}>
                                      <Td px={2} fontSize="11px" whiteSpace="nowrap">{r.entry_date}</Td>
                                      <Td px={2} fontSize="11px" maxW="200px" isTruncated>{r.description || '—'}</Td>
                                      <Td px={2} textAlign="right" fontSize="11px" color="green.600">{r.credit_amount != null ? fmtMoney(r.credit_amount) : '—'}</Td>
                                      <Td px={2} textAlign="right" fontSize="11px" color="red.600">{r.debit_amount != null ? fmtMoney(r.debit_amount) : '—'}</Td>
                                      <Td px={2} textAlign="right" fontSize="10px" color="gray.500">{r.balance != null ? fmtMoney(r.balance) : '—'}</Td>
                                    </Tr>
                                  ))}
                                  {importRows.length > 10 && (
                                    <Tr><Td colSpan={5} px={2} fontSize="10px" color="gray.400" py={2} textAlign="center">
                                      … and {importRows.length - 10} more rows
                                    </Td></Tr>
                                  )}
                                </Tbody>
                              </Table>
                            </Box>

                            <Flex justify="space-between" align="center">
                              <Button size="sm" variant="outline" leftIcon={<FiUpload size={12}/>}
                                onClick={() => fileRef.current?.click()}>
                                Choose different file
                              </Button>
                              <Button colorScheme="blue" leftIcon={<FiCheckCircle size={14}/>}
                                onClick={handleImport} isLoading={importing} loadingText="Importing…">
                                Import {importRows.length} Entries
                              </Button>
                            </Flex>
                          </Box>
                        )}
                      </Box>
                    </TabPanel>
                  )}

                </TabPanels>
              </Tabs>
            )}

            {!selectedAccount && (
              <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>Select a bank account to view its ledger.</Text>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
